import { useEffect, useState } from "react";
import { LocalizedLink as Link } from "@/components/routing/LocalizedLink";
import { useLocalizedNavigate as useNavigate } from "@/hooks/useLocalizedNavigate";
import { z } from "zod";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { AuthSplitLayout } from "@/components/auth/AuthSplitLayout";
import { Seo } from "@/components/seo/Seo";
import { scorePassword } from "@/lib/passwordStrength";
import { track } from "@/lib/analytics-plausible";
import { NewsletterOptIn } from "@/components/newsletter/NewsletterOptIn";

const schema = z.object({
  firstName: z.string().trim().min(1).max(50),
  lastName: z.string().trim().min(1).max(50),
  email: z.string().trim().email().max(255),
  password: z.string().min(8).max(72),
  terms: z.literal(true),
});

const Signup = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [terms, setTerms] = useState(false);
  const [newsletter, setNewsletter] = useState(false);
  const [topics, setTopics] = useState<string[]>([]);
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) navigate("/onboarding/role", { replace: true });
    });
  }, [navigate]);

  const strength = scorePassword(password);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse({ firstName, lastName, email, password, terms });
    if (!parsed.success) {
      toast.error(t("auth.validation.signupInvalid"));
      return;
    }
    setLoading(true);
    // Country + UI-language preference are collected at /onboarding/persona
    // so the signup form stays minimal (4 fields + ToS).
    const lang = i18n.language?.slice(0, 2) || "en";
    const { error } = await supabase.auth.signUp({
      email: parsed.data.email,
      password: parsed.data.password,
      options: {
        emailRedirectTo: `${window.location.origin}/${lang}/onboarding/role`,
        data: {
          first_name: parsed.data.firstName,
          last_name: parsed.data.lastName,
          language: lang,
        },
      },
    });
    setLoading(false);
    if (error) {
      if (error.message.toLowerCase().includes("registered")) {
        toast.error(t("auth.errors.emailTaken"));
      } else {
        toast.error(error.message);
      }
      return;
    }
    track.signup();
    toast.success(t("auth.toasts.checkInbox"));
    // Fire-and-forget newsletter opt-in
    if (newsletter) {
      supabase.functions.invoke("newsletter-subscribe", {
        body: {
          email: parsed.data.email,
          locale: lang,
          consent_source: "signup",
          topics,
          name: `${parsed.data.firstName} ${parsed.data.lastName}`.trim(),
        },
      }).catch((err) => console.warn("[Signup] newsletter opt-in failed", err));
    }
    const { data: { session } } = await supabase.auth.getSession();
    if (session) navigate("/onboarding/role", { replace: true });
  };

  const handleGoogle = async () => {
    setLoading(true);
    const lang = i18n.language?.slice(0, 2) || "en";
    const result = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: `${window.location.origin}/${lang}/onboarding/role`,
    });
    if (result.error) {
      setLoading(false);
      toast.error(t("auth.errors.googleFailed"));
    }
  };

  return (
    <>
      <Seo title={t("auth.signup.seoTitle")} description={t("auth.signup.seoDescription")} />
      <AuthSplitLayout
        side={
          <div>
            <div className="t-eyebrow text-primary">
              {t("auth.panel.label")}
            </div>
            <h2 className="mt-3 t-display-lg leading-[1.05]">
              {t("auth.signup.panelHeadline")}
            </h2>
            <p className="mt-4 t-body text-white/75">{t("auth.signup.panelBody")}</p>
            <ul className="mt-8 space-y-3 t-body-sm">
              {[1, 2, 3, 4].map((i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-1 grid h-5 w-5 flex-shrink-0 place-items-center rounded-full bg-primary/15 text-xs font-bold text-primary">
                    ✓
                  </span>
                  <span className="text-white/80">{t(`auth.signup.benefit${i}`)}</span>
                </li>
              ))}
            </ul>
          </div>
        }
      >
        <div className="t-eyebrow text-primary">{t("auth.createAccount", "Create account")}</div>
        <h1 className="mt-2 t-display-md text-text-1">{t("auth.signup.title")}</h1>
        <p className="mt-2 t-body-sm text-text-3">{t("auth.signup.subtitle")}</p>

        <Button
          type="button"
          variant="brandOutline"
          size="lg"
          className="mt-6 w-full"
          onClick={handleGoogle}
          disabled={loading}
        >
          {t("auth.continueWithGoogle")}
        </Button>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border-1" /></div>
          <div className="relative flex justify-center"><span className="bg-surface-1 px-3 t-eyebrow text-text-3">{t("auth.or")}</span></div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="firstName" className="t-body-sm font-medium text-text-2">{t("auth.fields.firstName")}</Label>
              <Input
                id="firstName"
                required
                maxLength={50}
                autoComplete="given-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="bg-surface-2 border-border-1"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName" className="t-body-sm font-medium text-text-2">{t("auth.fields.lastName")}</Label>
              <Input
                id="lastName"
                required
                maxLength={50}
                autoComplete="family-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="bg-surface-2 border-border-1"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email" className="t-body-sm font-medium text-text-2">{t("auth.fields.email")}</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-surface-2 border-border-1"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="t-body-sm font-medium text-text-2">{t("auth.fields.password")}</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPw ? "text" : "password"}
                autoComplete="new-password"
                required
                minLength={8}
                maxLength={72}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-surface-2 border-border-1 pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPw((s) => !s)}
                aria-label={showPw ? t("auth.hidePassword") : t("auth.showPassword")}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-text-3 hover:text-text-1"
              >
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {password ? (
              <div className="space-y-1">
                <div className="flex gap-1">
                  {[0, 1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className={`h-1 flex-1 rounded-full transition-colors ${
                        i < strength.score ? "bg-primary" : "bg-surface-3"
                      }`}
                    />
                  ))}
                </div>
                <p className="t-body-sm text-text-3">
                  {t(`auth.passwordStrength.${strength.label}`)}
                </p>
              </div>
            ) : (
              <p className="t-body-sm text-text-3">{t("auth.signup.passwordHint")}</p>
            )}
          </div>
          <div className="flex items-start gap-2">
            <Checkbox
              id="terms"
              checked={terms}
              onCheckedChange={(v) => setTerms(v === true)}
              className="mt-0.5"
            />
            <Label htmlFor="terms" className="t-body-sm font-normal text-text-3">
              {t("auth.signup.termsPrefix")}{" "}
              <Link to="/legal/terms" className="text-primary hover:underline underline-offset-4">
                {t("auth.signup.termsLink")}
              </Link>{" "}
              {t("auth.signup.and")}{" "}
              <Link to="/legal/privacy" className="text-primary hover:underline underline-offset-4">
                {t("auth.signup.privacyLink")}
              </Link>
              .
            </Label>
          </div>
          <NewsletterOptIn
            checked={newsletter}
            onCheckedChange={setNewsletter}
            topics={topics}
            onTopicsChange={setTopics}
            showTopics
          />
          <Button type="submit" variant="brand" size="xl" className="w-full" disabled={loading || !terms}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("auth.signup.submit")}
          </Button>
        </form>

        <p className="mt-6 text-center t-body-sm text-text-3">
          {t("auth.signup.haveAccount")}{" "}
          <Link to="/login" className="font-medium text-primary hover:underline underline-offset-4">
            {t("auth.signup.signInLink")}
          </Link>
        </p>
      </AuthSplitLayout>
    </>
  );
};

export default Signup;
